import { ReactNode } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { BdcDashboardStores, BdcLapsedLevel } from "../../../../types/bdcDashboard.type";
import { pcs } from "../../format";
import { CARD_SX, TABLE_SX } from "../../styles";
import Tile, { TileGrid } from "../Tile";
import { LEVEL_LABEL, percentText, rangeLabel, shareOf } from "../format";
import DcLapseBars from "./DcLapseBars";
import StoreTable, { CellLegend } from "./StoreTable";
import StreakHistogram from "./StreakHistogram";

const SECTION_SX = { ...CARD_SX, p: 2, display: "flex", flexDirection: "column", gap: 1.25, minWidth: 0 } as const;
const NOTE_SX = { fontSize: "0.75rem", color: "text.secondary" } as const;

const LEVEL_TILES: { level: BdcLapsedLevel; hint: string }[] = [
  { level: "late", hint: "ควรสั่งแล้ว ~1 ครั้ง" },
  { level: "missed", hint: "ควรสั่งแล้ว ≥ 2 ครั้ง" },
  { level: "gone", hint: "เงียบเกิน 3 เท่าของรอบปกติ" },
];

/**
 * Board 2 — which stores fell behind their OWN ordering habit. KPIs and charts cover the whole
 * base under the filters; the table lists the late / missed / gone stores (or the one level the
 * tiles pick), paged.
 */
export default function StoresView({
  data,
  branchOf,
  pagination,
  level,
  onLevel,
}: Readonly<{
  data: BdcDashboardStores;
  /** Given only when the caller sees more than one BDC branch. */
  branchOf?: (companyId: string) => string;
  pagination?: ReactNode;
  /** The level the list is narrowed to; the level tiles toggle it when onLevel is given. */
  level?: BdcLapsedLevel;
  onLevel?: (level: BdcLapsedLevel | undefined) => void;
}>) {
  const { kpis, min_lapse_days: minDays } = data;
  const windowText = rangeLabel(data.window.from, data.window.to);
  const listTitle = level ? LEVEL_LABEL[level] : "ร้านที่หลุดรอบการสั่ง";

  return (
    <Stack gap={1.75} sx={TABLE_SX}>
      <TileGrid columns={6}>
        <Tile
          label="ร้านในฐาน"
          value={pcs(kpis.base_stores)}
          sub={`สั่งอย่างน้อย 1 ครั้งใน ${windowText}`}
          foot={`ข้อมูลยังน้อย (สั่ง < 4 ครั้ง) ${pcs(kpis.thin)} ร้าน`}
        />
        <Tile
          label="สั่งวันนี้"
          value={pcs(kpis.ordered_today)}
          sub={`${percentText(shareOf(kpis.ordered_today, kpis.base_stores))} ของฐาน`}
        />
        {LEVEL_TILES.map((tile) => (
          <Tile
            key={tile.level}
            warn
            label={`⚠ ${LEVEL_LABEL[tile.level]}`}
            value={pcs(kpis.levels[tile.level].stores)}
            sub={tile.hint}
            foot={`ยอดที่หายไป ~${pcs(kpis.levels[tile.level].lost_volume)} ชิ้น`}
            selected={level === tile.level}
            onClick={onLevel ? () => onLevel(level === tile.level ? undefined : tile.level) : undefined}
          />
        ))}
        <Tile label="ร้านใหม่วันนี้" value={pcs(kpis.new_today)} sub="ไม่เคยสั่งมาก่อน" />
      </TileGrid>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.75 }}>
        <Card sx={{ ...SECTION_SX, flex: "1 1 520px" }}>
          <Typography fontWeight={600}>ร้านแยกตามจำนวนวันที่ไม่ได้สั่ง</Typography>
          <StreakHistogram buckets={data.histogram} />
          <Typography sx={NOTE_SX}>
            แกนนอน = วันที่ไม่ได้สั่งติดกันจนถึงวันที่เลือก · แท่งเข้ม = ≥{minDays} วัน
          </Typography>
        </Card>
        <Card sx={{ ...SECTION_SX, flex: "1 1 420px" }}>
          <Typography fontWeight={600}>สัดส่วนร้านที่หลุดรอบ แยก DC</Typography>
          <DcLapseBars rows={data.dc_lapse} branchOf={branchOf} />
          <Typography sx={NOTE_SX}>ร้านนับอยู่ใน DC ปัจจุบันของร้าน · ฐาน = ร้านที่สั่งใน {windowText}</Typography>
        </Card>
      </Box>

      <Card sx={{ ...CARD_SX }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          sx={{ px: 2, py: 1.5 }}
        >
          <Typography fontWeight={600}>
            {listTitle} {pcs(data.count)} ร้าน · เรียงตามยอดที่หายไป (ชิ้นต่อครั้ง × ครั้งที่ควรสั่งแล้ว)
          </Typography>
          <CellLegend />
        </Stack>
        <StoreTable rows={data.rows} branchOf={branchOf} />
        {pagination}
        <Typography sx={{ ...NOTE_SX, px: 2, pb: 1.5 }}>
          เทียบกับรอบสั่งปกติของแต่ละร้านใน {windowText} · ช่องสีขาว = DC ของร้านไม่มีไฟล์วันนั้น ไม่นับ ·
          Export ส่งให้ฝ่ายขาย/การตลาดไปเยี่ยมร้านหรือทำโปรโมชั่น
        </Typography>
      </Card>
    </Stack>
  );
}
